/* ============================================================
   THE TEDDY SOCIETY — Cart module
   File: assets/tts-cart.js
   Provides:
   - window.TTSCart  : locale-aware Cart AJAX API helpers
   - <tts-cart-drawer> custom element (accessible drawer)
   Events dispatched on document:
   - tts:cart:updated  detail: { cart }
   - tts:cart:added    detail: { item, cart }
   - tts:cart:error    detail: { message }
   ============================================================ */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function request(url, options) {
    options = options || {};
    options.headers = Object.assign(
      { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      options.headers || {}
    );
    return fetch(url, options).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) {
          var message = (json && (json.description || json.message)) || 'Something went wrong.';
          var error = new Error(message);
          error.status = response.status;
          error.body = json;
          throw error;
        }
        return json;
      });
    });
  }

  var TTSCart = {
    get: function () {
      return request(root + 'cart.js');
    },

    add: function (items) {
      return request(root + 'cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: items })
      }).then(function (result) {
        return TTSCart.get().then(function (cart) {
          var item = result.items ? result.items[0] : result;
          document.dispatchEvent(new CustomEvent('tts:cart:added', { detail: { item: item, cart: cart } }));
          document.dispatchEvent(new CustomEvent('tts:cart:updated', { detail: { cart: cart } }));
          return { item: item, cart: cart };
        });
      });
    },

    changeLine: function (lineKey, quantity) {
      return request(root + 'cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      }).then(function (cart) {
        document.dispatchEvent(new CustomEvent('tts:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
    }
  };

  window.TTSCart = TTSCart;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = (cents / 100).toFixed(2);
    var withThousands = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    format = format || '£{{amount}}';
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString());
  }
  window.TTSFormatMoney = formatMoney;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var focusable = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  var CartDrawer = function () {
    var instance = Reflect.construct(HTMLElement, [], CartDrawer);
    return instance;
  };
  CartDrawer.prototype = Object.create(HTMLElement.prototype);
  CartDrawer.prototype.constructor = CartDrawer;
  Object.setPrototypeOf(CartDrawer, HTMLElement);

  CartDrawer.prototype.connectedCallback = function () {
    this.overlay = this.querySelector('[data-tts-overlay]');
    this.panel = this.querySelector('[data-tts-drawer]');
    this.body = this.querySelector('[data-tts-drawer-body]');
    this.liveRegion = this.querySelector('[data-tts-live]');
    this.moneyFormat = this.getAttribute('data-money-format') || '£{{amount}}';
    this.sectionId = this.getAttribute('data-section-id');
    this.lastFocused = null;
    this.isOpen = false;

    var self = this;

    this.addEventListener('click', function (event) {
      if (event.target.closest('[data-tts-drawer-close]') || event.target === self.overlay) {
        self.close();
      }
      var removeBtn = event.target.closest('[data-tts-line-remove]');
      if (removeBtn) {
        event.preventDefault();
        self.changeQuantity(removeBtn.getAttribute('data-tts-line-remove'), 0, removeBtn);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!self.isOpen) return;
      if (event.key === 'Escape') self.close();
      if (event.key === 'Tab') trapFocus(self.panel, event);
    });

    document.addEventListener('tts:cart:added', function (event) {
      self.refresh().then(function () {
        self.open();
        self.announce((event.detail.item && event.detail.item.product_title ? event.detail.item.product_title + ' — ' : '') + 'added to your bag.');
      });
    });

    document.addEventListener('tts:cart:open', function () { self.refresh().then(function () { self.open(); }); });

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-tts-cart-open], a[href$="/cart"]');
      if (!trigger) return;
      if (trigger.closest('tts-cart-drawer')) return;
      event.preventDefault();
      self.refresh().then(function () { self.open(); });
    });

    this.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-tts-qty-change]');
      if (!btn) return;
      event.preventDefault();
      var line = btn.closest('[data-tts-line]');
      if (!line) return;
      var input = line.querySelector('[data-tts-qty-input]');
      var current = parseInt(input.value, 10) || 1;
      var next = btn.getAttribute('data-tts-qty-change') === 'up' ? current + 1 : current - 1;
      self.changeQuantity(line.getAttribute('data-tts-line'), Math.max(0, next), line);
    });

    this.addEventListener('change', function (event) {
      var input = event.target.closest('[data-tts-qty-input]');
      if (!input) return;
      var line = input.closest('[data-tts-line]');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 1;
      self.changeQuantity(line.getAttribute('data-tts-line'), qty, line);
    });
  };

  CartDrawer.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocused = document.activeElement;
    this.overlay.classList.add('is-open');
    this.panel.classList.add('is-open');
    this.panel.removeAttribute('aria-hidden');
    document.documentElement.style.overflow = 'hidden';
    var closeBtn = this.panel.querySelector('[data-tts-drawer-close]');
    if (closeBtn) closeBtn.focus();
  };

  CartDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
  };

  CartDrawer.prototype.announce = function (message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    var self = this;
    window.setTimeout(function () { self.liveRegion.textContent = message; }, 50);
  };

  CartDrawer.prototype.changeQuantity = function (lineKey, quantity, lineEl) {
    var self = this;
    if (lineEl && lineEl.classList) lineEl.classList.add('is-updating');
    TTSCart.changeLine(lineKey, quantity)
      .then(function () {
        return self.refresh();
      })
      .then(function () {
        self.announce(quantity === 0 ? 'Item removed from your bag.' : 'Bag updated.');
      })
      .catch(function (error) {
        if (lineEl && lineEl.classList) lineEl.classList.remove('is-updating');
        self.announce(error.message || 'We could not update your bag.');
      });
  };

  CartDrawer.prototype.refresh = function () {
    var self = this;
    if (!this.sectionId) return Promise.resolve();
    return fetch(root + '?sections=' + this.sectionId)
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections[self.sectionId];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var freshBody = parsed.querySelector('[data-tts-drawer-body]');
        var freshFoot = parsed.querySelector('[data-tts-drawer-foot]');
        var currentFoot = self.querySelector('[data-tts-drawer-foot]');
        if (freshBody && self.body) self.body.innerHTML = freshBody.innerHTML;
        if (freshFoot && currentFoot) currentFoot.innerHTML = freshFoot.innerHTML;
        var freshCount = parsed.querySelector('[data-tts-cart-count]');
        if (freshCount) self.updateBadges(freshCount.textContent.trim());
      })
      .catch(function () { /* non-fatal */ });
  };

  CartDrawer.prototype.updateBadges = function (count) {
    var badges = document.querySelectorAll('[data-tts-cart-count], .cart-count-bubble span[aria-hidden="true"]');
    badges.forEach(function (badge) { badge.textContent = count; });
  };

  if (!window.customElements.get('tts-cart-drawer')) {
    window.customElements.define('tts-cart-drawer', CartDrawer);
  }
})();
